import { useState, useEffect, useRef, useMemo, useCallback } from "react";

/**
 * Grid-based image loading animation.
 * Adapted from 21st.dev/tonyzebastian/image-loading for CRA + plain CSS.
 */
export default function ImageLoader({
  src,
  alt = "",
  gridSize = 20,
  cellShape = "circle",
  cellGap = 2,
  cellColor = "#1a3a3a",
  blinkSpeed = 1000,
  transitionDuration = 800,
  fadeOutDuration = 600,
  loadingDelay = 1500,
  onLoad,
  className = "",
  width,
  height,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [showImage, setShowImage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [gridCells, setGridCells] = useState([]);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const processedRef = useRef(false);
  const loadStartTimeRef = useRef(Date.now());

  const dimensions = useMemo(
    () => ({
      width: parseInt(String(width)) || 400,
      height: parseInt(String(height)) || 400,
    }),
    [width, height]
  );

  const [containerSize, setContainerSize] = useState({ width: 500, height: 500 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width: w, height: h } = entry.contentRect;
      if (w > 0 && h > 0) setContainerSize({ width: Math.round(w), height: Math.round(h) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Generate grid cells
  useEffect(() => {
    if (containerSize.width === 0 || containerSize.height === 0) return;

    const cellWithGap = gridSize + cellGap;
    const cols = Math.ceil(containerSize.width / cellWithGap) + 1;
    const rows = Math.ceil(containerSize.height / cellWithGap) + 1;

    const cells = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        cells.push({
          id: `${row}-${col}`,
          x: col * cellWithGap,
          y: row * cellWithGap,
          blinkDelay: Math.random() * blinkSpeed,
          fadeDelay: Math.random() * fadeOutDuration,
          initialOpacity: Math.random() * 0.7 + 0.3,
          color: null,
        });
      }
    }

    setGridCells(cells);
  }, [containerSize.width, containerSize.height, gridSize, cellGap, blinkSpeed, fadeOutDuration]);

  // Sample color from a region of the canvas
  const sampleColorFromRegion = useCallback(
    (canvas, x, y, w, h) => {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return cellColor;

      const safeX = Math.min(x, canvas.width - 1);
      const safeY = Math.min(y, canvas.height - 1);
      const safeW = Math.min(w, canvas.width - safeX);
      const safeH = Math.min(h, canvas.height - safeY);
      if (safeW <= 0 || safeH <= 0) return cellColor;

      const imageData = ctx.getImageData(safeX, safeY, safeW, safeH);
      const data = imageData.data;

      let r = 0,
        g = 0,
        b = 0,
        count = 0;

      for (let i = 0; i < data.length; i += 16) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }

      if (count === 0) return cellColor;
      return `rgb(${Math.round(r / count)}, ${Math.round(g / count)}, ${Math.round(b / count)})`;
    },
    [cellColor]
  );

  // Process the image once loaded
  const processImage = useCallback(
    (img, currentGridCells) => {
      if (processedRef.current || currentGridCells.length === 0) return;
      processedRef.current = true;

      const doProcess = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        ctx.drawImage(img, 0, 0);

        const scaleX = img.naturalWidth / containerSize.width;
        const scaleY = img.naturalHeight / containerSize.height;

        const updatedCells = currentGridCells.map((cell) => ({
          ...cell,
          color: sampleColorFromRegion(
            canvas,
            Math.floor(cell.x * scaleX),
            Math.floor(cell.y * scaleY),
            Math.floor(gridSize * scaleX),
            Math.floor(gridSize * scaleY)
          ),
        }));

        setGridCells(updatedCells);
        setIsLoading(false);
        setIsTransitioning(true);

        setTimeout(() => setShowImage(true), transitionDuration);
        setTimeout(() => {
          setIsTransitioning(false);
          setIsFadingOut(true);
        }, transitionDuration);

        onLoad?.();
      };

      if (loadingDelay > 0) {
        const elapsed = Date.now() - loadStartTimeRef.current;
        const remaining = Math.max(0, loadingDelay - elapsed);
        setTimeout(doProcess, remaining);
      } else {
        doProcess();
      }
    },
    [containerSize, gridSize, transitionDuration, loadingDelay, sampleColorFromRegion, onLoad]
  );

  // Detect image load
  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    if (img.complete && img.naturalWidth > 0) {
      processImage(img, gridCells);
    } else {
      const handleLoad = () => processImage(img, gridCells);
      img.addEventListener("load", handleLoad);
      return () => img.removeEventListener("load", handleLoad);
    }
  }, [gridCells, processImage]);

  // Reset when src changes
  useEffect(() => {
    processedRef.current = false;
    loadStartTimeRef.current = Date.now();
    setIsLoading(true);
    setShowImage(false);
    setIsTransitioning(false);
    setIsFadingOut(false);
  }, [src]);

  const getCellStyle = useCallback(
    (cell) => {
      const base = {
        position: "absolute",
        left: cell.x,
        top: cell.y,
        borderRadius: cellShape === "circle" ? "50%" : "3px",
        willChange: "opacity, background-color, width, height, left, top",
      };

      if (isLoading) {
        return {
          ...base,
          animation: `imgLoaderBlink ${blinkSpeed}ms infinite`,
          animationDelay: `${cell.blinkDelay}ms`,
          animationFillMode: "backwards",
          backgroundColor: cellColor,
          width: gridSize,
          height: gridSize,
          opacity: cell.initialOpacity,
        };
      }

      if (isTransitioning) {
        return {
          ...base,
          backgroundColor: cell.color || cellColor,
          transition: `background-color ${transitionDuration}ms ease, width ${transitionDuration}ms ease, height ${transitionDuration}ms ease, left ${transitionDuration}ms ease, top ${transitionDuration}ms ease, opacity ${transitionDuration}ms ease`,
          width: gridSize + cellGap,
          height: gridSize + cellGap,
          left: cell.x - cellGap / 2,
          top: cell.y - cellGap / 2,
          opacity: 1,
          animation: "none",
        };
      }

      if (isFadingOut) {
        return {
          ...base,
          backgroundColor: cell.color || cellColor,
          opacity: 0,
          transition: `opacity ${fadeOutDuration}ms ease`,
          transitionDelay: `${cell.fadeDelay}ms`,
          width: gridSize + cellGap,
          height: gridSize + cellGap,
          left: cell.x - cellGap / 2,
          top: cell.y - cellGap / 2,
        };
      }

      return base;
    },
    [isLoading, isTransitioning, isFadingOut, blinkSpeed, cellColor, gridSize, cellGap, transitionDuration, fadeOutDuration, cellShape]
  );

  return (
    <div className={`img-loader-wrap ${className}`}>
      <div
        ref={containerRef}
        className="img-loader-container"
        style={{
          width: width || "100%",
          aspectRatio: `${dimensions.width} / ${dimensions.height}`,
        }}
      >
        {/* Grid overlay */}
        {gridCells.length > 0 && (
          <div className="img-loader-grid">
            {gridCells.map((cell) => (
              <div key={cell.id} style={getCellStyle(cell)} />
            ))}
          </div>
        )}

        {/* Actual image */}
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          crossOrigin="anonymous"
          className="img-loader-img"
          style={{
            opacity: showImage ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
        />
      </div>
    </div>
  );
}
